import { useRef, useState } from "react"
import { useDialogFocus } from "./useDialogFocus"

export type ConflictChoice =
  | { kind: "mine" }
  | { kind: "agent" }
  | { kind: "merged"; markdown: string }

interface Props {
  sectionLabel: string
  mine: string
  agent: string
  onResolve: (choice: ConflictChoice) => void
  /** Escape: close without choosing. The edit stays in the editor and the section keeps asking. */
  onClose: () => void
}

/** The agent saved this section while the user was editing it. Pick mine, the agent's, or merge by copy. */
export function ConflictDialog({ sectionLabel, mine, agent, onResolve, onClose }: Props) {
  const [merging, setMerging] = useState(false)
  const [merged, setMerged] = useState(`${agent}\n\n${mine}`)
  const first = useRef<HTMLButtonElement>(null)
  useDialogFocus(onClose, first)
  const copy = (text: string) => { void navigator.clipboard?.writeText(text).catch(() => undefined) }

  return (
    <div className="osim-scrim">
      <div className="osim-dialog" role="dialog" aria-modal="true" aria-labelledby="osim-conflict-title">
        <p className="ov">SAVE CONFLICT</p>
        <h3 id="osim-conflict-title">{sectionLabel} changed while you were editing</h3>
        <p className="tk">The agent saved a newer version. Choose which one to keep.</p>
        <div className="osim-sides">
          <section aria-label="Your version">
            <h4 className="lab">Your version</h4>
            <pre className="osim-pre" data-testid="conflict-mine">{mine}</pre>
            <button type="button" className="sbtn" onClick={() => copy(mine)}>Copy</button>
          </section>
          <section aria-label="Agent's version">
            <h4 className="lab">Agent's version</h4>
            <pre className="osim-pre" data-testid="conflict-agent">{agent}</pre>
            <button type="button" className="sbtn" onClick={() => copy(agent)}>Copy</button>
          </section>
        </div>
        {merging ? (
          <div className="osim-merge">
            <label className="lab" htmlFor="osim-merge-ta">Merged text</label>
            <textarea id="osim-merge-ta" className="osim-reply-ta" rows={8} value={merged} onChange={(e) => setMerged(e.target.value)} />
            <div className="cacts">
              <button type="button" className="pbtn" disabled={!merged.trim()} onClick={() => onResolve({ kind: "merged", markdown: merged })}>Save merged</button>
              <button type="button" className="sbtn" onClick={() => setMerging(false)}>Back</button>
            </div>
          </div>
        ) : (
          <div className="cacts">
            <button ref={first} type="button" className="pbtn" onClick={() => onResolve({ kind: "mine" })}>Keep mine</button>
            <button type="button" className="sbtn" onClick={() => onResolve({ kind: "agent" })}>Use agent's</button>
            <button type="button" className="sbtn" onClick={() => setMerging(true)}>Merge by copy</button>
          </div>
        )}
      </div>
    </div>
  )
}
